/**
 * Připomínky a hlášení chyb od uživatelů.
 * Zprávy jdou do schránky autora na Cloudflare (worker/pripominky.js) – žádný e‑mail.
 * Bez signálu se zpráva uloží v telefonu a odešle se, až bude připojení.
 * Autor si je čte v aplikaci po zadání svého hesla (obrazovka Schránka).
 */
import { useEffect, useState } from 'react';
import { getSetting, setSetting } from '../db/db';
import { back, go, useRoute } from './hooks';
import { Chips, Empty, Field, Notice, Screen, Section, TextInput, toast } from './ui';
import { saved } from './confirm';
import { Icon } from './icons';

declare const __APP_VERSION__: string;

import { PushSetting } from './push';
export const FEEDBACK_URL = 'https://vcely-pripominky.ulnik.workers.dev/feedback';
const QUEUE = 'feedbackQueue';
const LAST_ERROR = 'vcely-last-error';
const ADMIN_KEY = 'vcely-feedback-admin';

type Kind = 'bug' | 'idea' | 'other';
const KINDS: [Kind, string][] = [
  ['bug', 'Chyba'],
  ['idea', 'Návrh na vylepšení'],
  ['other', 'Jiné'],
];
const KIND_LABEL: Record<string, string> = Object.fromEntries(KINDS);

interface Payload {
  kind: Kind;
  message: string;
  contact?: string;
  version?: string;
  device?: string;
  screen?: string;
  error?: string;
}

type Queued = Payload & { qid: string };

interface Received extends Payload {
  id: string;
  receivedAt: string;
  country?: string;
}

// ---------------------------------------------------------------------------
// Zachycení chyb (poslední chyba se nabídne k odeslání)
// ---------------------------------------------------------------------------

export function recordError(e: unknown) {
  try {
    const err = e as { message?: string; stack?: string };
    const text = [
      new Date().toISOString(),
      location.hash || '#/',
      String(err?.message ?? e),
      (err?.stack ?? '').split('\n').slice(0, 6).join('\n'),
    ].join('\n');
    localStorage.setItem(LAST_ERROR, text.slice(0, 2000));
  } catch {
    /* nevadí */
  }
}

function lastError(): string | undefined {
  try {
    return localStorage.getItem(LAST_ERROR) ?? undefined;
  } catch {
    return undefined;
  }
}

export function installErrorCapture() {
  addEventListener('error', (e) => recordError(e.error ?? e.message));
  addEventListener('unhandledrejection', (e) => recordError(e.reason));
  addEventListener('online', () => void flushQueue());
  setTimeout(() => void flushQueue(), 3000);
}

// ---------------------------------------------------------------------------
// Odeslání a fronta pro offline
// ---------------------------------------------------------------------------

async function post(p: Payload): Promise<boolean> {
  try {
    const r = await fetch(FEEDBACK_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(p) });
    return r.ok;
  } catch {
    return false;
  }
}

let flushing: Promise<void> | null = null;

/** Odešle zprávy čekající ve frontě. Běží vždy jen jednou naráz. */
export function flushQueue(): Promise<void> {
  if (!flushing) {
    flushing = (async () => {
      const q = (await getSetting<Queued[]>(QUEUE)) ?? [];
      if (!q.length || !navigator.onLine) return;
      const sent = new Set<string>();
      for (const { qid, ...p } of q) if (await post(p)) sent.add(qid);
      // znovu načíst – mezitím mohla přibýt nová zpráva
      const now = (await getSetting<Queued[]>(QUEUE)) ?? [];
      await setSetting(QUEUE, now.filter((x) => !sent.has(x.qid)));
    })().finally(() => {
      flushing = null;
    });
  }
  return flushing;
}

async function enqueue(p: Payload) {
  const q = (await getSetting<Queued[]>(QUEUE)) ?? [];
  await setSetting(QUEUE, [...q, { ...p, qid: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}` }]);
}

function deviceInfo() {
  const standalone = matchMedia?.('(display-mode: standalone)').matches || (navigator as unknown as { standalone?: boolean }).standalone;
  return `${navigator.userAgent} · ${screen.width}×${screen.height} · ${standalone ? 'na ploše' : 'v prohlížeči'} · ${navigator.language}`;
}

// ---------------------------------------------------------------------------
// Obrazovka Připomínky
// ---------------------------------------------------------------------------

export function FeedbackScreen() {
  const { query } = useRoute();
  const fromError = query.has('chyba');
  const err = lastError();
  const [kind, setKind] = useState<Kind>(fromError ? 'bug' : 'idea');
  const [message, setMessage] = useState('');
  const [contact, setContact] = useState('');
  const [withTech, setWithTech] = useState(true);
  const [withError, setWithError] = useState(fromError && !!err);
  const [sending, setSending] = useState(false);
  const [queued, setQueued] = useState(0);
  useEffect(() => {
    getSetting<Payload[]>(QUEUE).then((q) => setQueued(q?.length ?? 0));
  }, []);

  const send = async () => {
    if (!message.trim()) return toast('Napište, co se stalo nebo co navrhujete.');
    const p: Payload = {
      kind,
      message: message.trim(),
      contact: contact.trim() || undefined,
      version: __APP_VERSION__,
      device: withTech ? deviceInfo() : undefined,
      error: withError ? err : undefined,
    };
    setSending(true);
    const ok = navigator.onLine && (await post(p));
    setSending(false);
    if (ok) {
      if (withError) {
        try {
          localStorage.removeItem(LAST_ERROR);
        } catch {
          /* nevadí */
        }
      }
      saved('Děkujeme, odesláno');
    } else {
      await enqueue(p);
      saved('Uloženo – odešle se po připojení');
    }
    back('nastaveni');
  };

  return (
    <Screen title="Připomínky a chyby" subtitle="Pomozte aplikaci vylepšit" backTo="nastaveni">
      <p className="muted">
        Napište, co nefunguje, co vám chybí nebo co by šlo udělat lépe. Zpráva jde přímo autorovi aplikace. Vaše data o včelstvech se
        neposílají – jen to, co sem napíšete.
      </p>
      {queued > 0 && <Notice tone="info">Čeká na odeslání: {queued} {queued === 1 ? 'zpráva' : 'zprávy'} (odešlou se po připojení).</Notice>}
      <Field label="O co jde">
        <Chips ariaLabel="O co jde" options={KINDS} value={kind} onChange={(v) => v && setKind(v)} />
      </Field>
      <Field label="Zpráva" htmlFor="fb-msg">
        <TextInput
          id="fb-msg"
          multiline
          value={message}
          onChange={setMessage}
          placeholder={kind === 'bug' ? 'Co jste dělali a co se stalo? Např. „Po uložení krmení se nezobrazil součet.“' : 'Co by vám pomohlo?'}
        />
      </Field>
      <Field label="Kontakt pro odpověď (nepovinné)" htmlFor="fb-contact" hint="E‑mail nebo telefon, pokud chcete odpověď.">
        <TextInput id="fb-contact" value={contact} onChange={setContact} />
      </Field>
      <label className="check">
        <input type="checkbox" checked={withTech} onChange={(e) => setWithTech(e.target.checked)} />
        Přiložit technické údaje (verze aplikace, typ telefonu)
      </label>
      {err && (
        <label className="check">
          <input type="checkbox" checked={withError} onChange={(e) => setWithError(e.target.checked)} />
          Přiložit poslední zachycenou chybu
        </label>
      )}
      {withError && err && <pre className="tech-box">{err}</pre>}
      <div className="quick-row">
        <button className="btn primary big wide" disabled={sending} onClick={send}>
          {sending ? 'Odesílám…' : 'Odeslat'}
        </button>
      </div>
      <p className="small muted center">
        Verze {__APP_VERSION__} ·{' '}
        <button type="button" className="btn link small-link" onClick={() => go('pripominky/schranka')}>
          pro autora aplikace
        </button>
      </p>
    </Screen>
  );
}

// ---------------------------------------------------------------------------
// Schránka autora
// ---------------------------------------------------------------------------

function getKey() {
  try {
    return localStorage.getItem(ADMIN_KEY) ?? '';
  } catch {
    return '';
  }
}

export function FeedbackInboxScreen() {
  const [key, setKey] = useState(getKey);
  const [input, setInput] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [items, setItems] = useState<Received[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const load = async (k = key) => {
    setError(null);
    try {
      const r = await fetch(FEEDBACK_URL, { headers: { Authorization: `Bearer ${k}` } });
      if (r.status === 401) {
        setError('Heslo nesedí.');
        setItems(null);
        return false;
      }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setItems(((await r.json()) as { items: Received[] }).items);
      return true;
    } catch (e) {
      setError(`Schránku se nepodařilo načíst (${String((e as Error).message ?? e)}). Je telefon připojený?`);
      return false;
    }
  };

  useEffect(() => {
    if (key) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remove = async (id: string) => {
    const r = await fetch(`${FEEDBACK_URL}/${encodeURIComponent(id)}`, { method: 'DELETE', headers: { Authorization: `Bearer ${key}` } }).catch(() => null);
    if (r?.ok) {
      setItems((xs) => xs?.filter((x) => x.id !== id) ?? null);
      saved('Smazáno');
    } else toast('Smazání se nepovedlo.');
  };

  if (!key || (error === 'Heslo nesedí.' && !items)) {
    return (
      <Screen title="Schránka připomínek" backTo="pripominky">
        <p className="muted">
          Jen pro autora aplikace. Zadejte heslo schránky – stejné jako v řádku <code>const HESLO = '…'</code> v Cloudflare (záleží na
          velkých a malých písmenech). Uloží se jen v tomto telefonu.
        </p>
        {error && <Notice tone="warn">{error}</Notice>}
        <Field label="Heslo" htmlFor="fb-key">
          <TextInput id="fb-key" type={showKey ? 'text' : 'password'} plain value={input} onChange={setInput} />
        </Field>
        <label className="check">
          <input type="checkbox" checked={showKey} onChange={(e) => setShowKey(e.target.checked)} />
          Zobrazit heslo{input.trim() ? ` (${input.trim().length} znaků)` : ''}
        </label>
        <button
          className="btn primary wide"
          onClick={async () => {
            const k = input.trim();
            if (!k) return;
            if (await load(k)) {
              try {
                localStorage.setItem(ADMIN_KEY, k);
              } catch {
                /* nevadí */
              }
              setKey(k);
            }
          }}
        >
          Otevřít schránku
        </button>
      </Screen>
    );
  }

  return (
    <Screen
      title="Schránka připomínek"
      subtitle={items ? `${items.length} ${items.length === 1 ? 'zpráva' : items.length > 1 && items.length < 5 ? 'zprávy' : 'zpráv'}` : undefined}
      backTo="pripominky"
    >
      <div className="quick-row">
        <button className="btn" onClick={() => load()}>
          <Icon name="refresh" size={20} /> Obnovit
        </button>
        <button className="btn" onClick={() => go('pripominky/statistiky')}>
          <Icon name="chart" size={20} /> Statistika
        </button>
        <button
          className="btn"
          onClick={() => {
            try {
              localStorage.removeItem(ADMIN_KEY);
            } catch {
              /* nevadí */
            }
            setKey('');
            setItems(null);
          }}
        >
          Odhlásit
        </button>
      </div>
      {error && <Notice tone="warn">{error}</Notice>}
      {items && items.length === 0 && <Empty>Zatím žádné připomínky.</Empty>}
      {items && items.length > 0 && (
        <Section>
          <ul className="cards">
            {items.map((x) => (
              <li key={x.id} className="card fb-item">
                <div className="fb-head">
                  <span className={'badge ' + (x.kind === 'bug' ? 'warn' : x.kind === 'idea' ? 'honey' : 'info')}>{KIND_LABEL[x.kind] ?? 'Jiné'}</span>
                  <span className="muted small">
                    {new Date(x.receivedAt).toLocaleString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })} · v{x.version ?? '?'}
                  </span>
                </div>
                <p className="fb-msg selectable">{x.message}</p>
                {x.contact && <p className="small">Kontakt: {x.contact}</p>}
                {(x.device || x.error) && (
                  <button type="button" className="btn link" onClick={() => setOpen(open === x.id ? null : x.id)}>
                    {open === x.id ? 'Skrýt technické údaje' : 'Technické údaje'}
                  </button>
                )}
                {open === x.id && (
                  <pre className="tech-box">
                    {[x.device, x.country ? `země: ${x.country}` : '', x.error].filter(Boolean).join('\n\n')}
                  </pre>
                )}
                <div className="quick-row">
                  <button className="btn small danger" onClick={() => remove(x.id)}>
                    Smazat
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}
      <PushSetting adminKey={key} />
    </Screen>
  );
}
